import React, { useState, useRef, useEffect } from 'react';
import {
  User,
  Settings,
  Zap,
  CreditCard,
  LogOut,
  BarChart3,
  X,
  Download,
  Calendar,
  Crown,
  FileText,
  ChevronRight,
  Shield,
  Accessibility,
  History
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, trackLogout } from '../utils/GoogleAnalytics';

/**
 * UserProfileDropdown - Compact Version
 * ✅ Updated: Uses utils/GoogleAnalytics, Added Accessibility link
 * ✅ FIXED: Usage History now navigates to profile tab instead of modal
 */
function UserProfileDropdown({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleNavigateToProfile = (tab = 'overview') => {
    setIsOpen(false);
    navigate(`/profile?tab=${tab}`);
    trackEvent('Profile', 'Navigate', tab);
  };

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
    trackEvent('Navigation', 'Profile Menu', path);
  };

  const handleLogoutClick = () => {
    trackLogout();
    onLogout();
    setIsOpen(false);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) trackEvent('Profile', 'Open Dropdown');
        }}
        className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        title={user.email}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Open user menu"
      >
        <span className="text-white font-bold text-sm" aria-hidden="true">
          {user.email?.charAt(0).toUpperCase()}
        </span>
      </button>

      {/* DROPDOWN MENU */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
          role="menu"
          aria-orientation="vertical"
        >

          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 px-4 py-3 text-white">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <span className="text-white font-bold">
                  {user.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">
                  {user.email?.split('@')[0]}
                </p>
                <p className="text-xs text-blue-100 truncate">{user.email}</p>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2" aria-label={`${user.credits || 0} credits available`}>
              <Zap size={16} className="text-yellow-500" aria-hidden="true" />
              <span className="font-bold text-gray-900">{user.credits || 0}</span>
              <span className="text-xs text-gray-500">credits</span>
            </div>
            <div className="flex items-center space-x-2">
              <Crown size={16} className={user.is_premium ? 'text-purple-500' : 'text-gray-400'} aria-hidden="true" />
              <span className={`text-sm font-medium capitalize ${user.is_premium ? 'text-purple-600' : 'text-gray-600'}`}>
                {user.plan_tier || 'Free'}
              </span>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1" role="none">
            <button
              onClick={() => handleNavigateToProfile('overview')}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-100"
              role="menuitem"
            >
              <BarChart3 size={16} className="text-blue-500" aria-hidden="true" />
              <span className="text-sm">My Profile</span>
            </button>

            {/* ✅ FIXED: Navigate to history tab instead of opening modal */}
            <button
              onClick={() => handleNavigateToProfile('history')}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-100"
              role="menuitem"
            >
              <History size={16} className="text-green-500" aria-hidden="true" />
              <span className="text-sm">Usage History</span>
            </button>

            <button
              onClick={() => handleNavigateToProfile('billing')}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-100"
              role="menuitem"
            >
              <CreditCard size={16} className="text-purple-500" aria-hidden="true" />
              <span className="text-sm">Credits & Plans</span>
            </button>

            <button
              onClick={() => handleNavigateToProfile('settings')}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-100"
              role="menuitem"
            >
              <Settings size={16} className="text-gray-500" aria-hidden="true" />
              <span className="text-sm">Settings</span>
            </button>
          </div>

          {/* Upgrade Button */}
          {!user.is_premium && (
            <div className="px-3 pb-2">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/pricing');
                  trackEvent('Pricing', 'Upgrade Click', 'dropdown');
                }}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-sm font-semibold hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                role="menuitem"
              >
                <Zap size={14} aria-hidden="true" />
                <span>Upgrade to Pro</span>
              </button>
            </div>
          )}

          {/* Legal Links - Including Accessibility */}
          <div className="border-t border-gray-200 px-4 py-2 flex items-center justify-center space-x-2 text-xs text-gray-400">
            <button
              onClick={() => handleNavigate('/privacy')}
              className="hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
              role="menuitem"
            >
              Privacy
            </button>
            <span aria-hidden="true">•</span>
            <button
              onClick={() => handleNavigate('/terms')}
              className="hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
              role="menuitem"
            >
              Terms
            </button>
            <span aria-hidden="true">•</span>
            <button
              onClick={() => handleNavigate('/accessibility')}
              className="hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
              role="menuitem"
            >
              Accessibility
            </button>
            <span aria-hidden="true">•</span>
            <button
              onClick={() => {
                setIsOpen(false);
                localStorage.removeItem('cookieConsent');
                window.location.reload();
              }}
              className="hover:text-gray-600 transition-colors focus:outline-none focus:text-gray-600"
              role="menuitem"
              aria-label="Manage cookie preferences"
            >
              Cookies
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-200">
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:bg-red-100"
              role="menuitem"
            >
              <LogOut size={16} aria-hidden="true" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfileDropdown;